import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import useAntibodyMapState from 'antibodies/antibody-map/useAntibodyMapState';
import {INTERACTIVE_WRAPPER_DATA_ATTR} from 'antibodies/visualization/InteractiveComponentWrapper';
import useTransientCurrentPolymerModel from 'molbio/hooks/useTransientCurrentPolymerModel';
import ProteinMapContainer from 'molbio/maps/standalone/ProteinMapContainer';
import Protein from 'molbio/proteins/Protein';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ClickOutWrapper from 'ui-library/ClickOutWrapper';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export interface ComponentPreviewProps {
  antibodyId: string;
  selectedProteinId: string;
  toggleExpandPreview: () => void;
  closePreview: () => void;
}

export default function ComponentPreview({
  antibodyId,
  selectedProteinId,
  toggleExpandPreview,
  closePreview,
}: ComponentPreviewProps): JSX.Element {
  const protein = useTransientCurrentPolymerModel(selectedProteinId) as Protein | null;
  const {
    state: {expandPreview, showMinimap},
    setShowMinimap,
  } = useAntibodyMapState(antibodyId);

  function onClickOut(e: MouseEvent): void {
    // Do not close the preview if any floating menus, modals, etc are open from the preview
    // or if the user is interacting with an interactive component wrapper (i.e. a domain in the antibody map
    // or a chain in the antibody chain legend). Clicking on the interactive component will already close the preview.
    const clickedInteractiveComponent =
      $(e.target as Element).parents(`[${INTERACTIVE_WRAPPER_DATA_ATTR}]`).length > 0;
    if (
      $('.modal-open').length === 0 &&
      $('#context-menu:visible').length === 0 &&
      !$(e.target as Element).hasClass('backdrop') &&
      $(e.target as Element).parents('.absoluteDropdownContainer').length === 0 &&
      !clickedInteractiveComponent
    ) {
      closePreview();
    }
  }

  return (
    <ClickOutWrapper onClickOut={onClickOut}>
      <div className={css(styles.wrapper)}>
        <div className={css(styles.header)}>
          <BenchChip id={selectedProteinId} size={ChipSize.SMALL} />
          <div className={css(styles.buttonsWrapper)}>
            {expandPreview && (
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                className={showMinimap ? css(styles.activeMinimap) : ''}
                icon="antibody"
                iconAltText={showMinimap ? 'Hide minimap' : 'Show minimap'}
                onClick={() => {
                  setShowMinimap(!showMinimap);
                }}
                size={ButtonSize.SMALL}
              />
            )}
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              icon={expandPreview ? 'collapse-right-full' : 'expand-right-full'}
              iconAltText={expandPreview ? 'Collapse component preview' : 'Expand component preview'}
              onClick={toggleExpandPreview}
              size={ButtonSize.SMALL}
            />
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              icon="cross-thick"
              iconAltText="Close component preview"
              onClick={closePreview}
              size={ButtonSize.SMALL}
            />
          </div>
        </div>
        <div className={css(expandPreview ? styles.proteinMapWrapperExpanded : styles.proteinMapWrapper)}>
          {protein == null && <CenteredHorizontalThrobber />}
          {protein != null && <ProteinMapContainer protein={protein} />}
        </div>
      </div>
    </ClickOutWrapper>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    border: `1px solid ${Color.GRAY_100}`,
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    backgroundColor: Color.GRAY_100,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px',
    borderRadiusTopLeft: '4px',
    borderRadiusTopRight: '4px',
    flex: '0 0 44px',
  },
  buttonsWrapper: {
    display: 'flex',
    gap: '8px',
  },
  activeMinimap: {
    color: Color.BLUE_400,
  },
  icon: {
    cursor: 'pointer',
    width: '16px',
    height: '16px',
  },
  proteinMapWrapper: {
    flex: '0 0 236px',
  },
  proteinMapWrapperExpanded: {
    flex: '1 0 auto',
  },
});
